/**
 * ETQ-01 — Plantillas de etiqueta de producto.
 *
 * El contenido de la etiqueta es configurable porque depende del rubro: en un
 * catálogo de repuestos lo que el bodeguero necesita leer es la procedencia y el
 * código original, que viven en `Product.attributes` y son dinámicos por
 * categoría — no se pueden hornear en el componente.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

/** Una línea de la etiqueta. El ORDEN del arreglo es el orden en la impresión. */
export interface LabelField {
  /** `name` | `sku` | `barcode` | `price` | `unit` | `category` | `description` | `attr:<código>` */
  key: string
  font_size?: number
  bold?: boolean
  muted?: boolean
}

export interface LabelTemplate {
  id: string
  name: string
  /** Ancho ÚTIL del rollo o la hoja, en mm. */
  width_mm: number
  height_mm: number
  /** 1 = ancho completo · 2 = el ancho dividido en dos. */
  columns: number
  gap_mm: number
  barcode_type: 'code128' | 'ean13'
  fields: LabelField[]
  is_default: boolean
  is_active: boolean
}

export interface LabelFieldOption {
  key: string
  label: string
  grupo: string
}

export function useLabelTemplates(enabled = true) {
  return useQuery<{ templates: LabelTemplate[] }>({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get('/config/label-templates/')).data,
    enabled,
    staleTime: 1000 * 60 * 10,
  })
}

/** Campos que ESTE tenant puede poner en la etiqueta (incluye sus atributos). */
export function useLabelFields(enabled = true) {
  return useQuery<{ fields: LabelFieldOption[] }>({
    queryKey: ['label-templates', 'fields'],
    queryFn: async () => (await apiClient.get('/config/label-templates/fields/')).data,
    enabled,
    staleTime: 1000 * 60 * 30,
  })
}

export function useSaveLabelTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...datos }: Partial<LabelTemplate> & { id?: string }) =>
      id
        ? (await apiClient.patch(`/config/label-templates/${id}/`, datos)).data
        : (await apiClient.post('/config/label-templates/', datos)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['label-templates'] }),
  })
}

export function useDeleteLabelTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/config/label-templates/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['label-templates'] }),
  })
}

/** La plantilla por defecto, o la primera activa. */
export function plantillaPorDefecto(ts?: LabelTemplate[]): LabelTemplate | null {
  if (!ts || ts.length === 0) return null
  return ts.find((t) => t.is_default) ?? ts[0]
}

/** Contenido por defecto cuando el tenant todavía no configuró ninguna plantilla. */
export const PLANTILLA_BASE: Pick<
  LabelTemplate, 'width_mm' | 'height_mm' | 'columns' | 'gap_mm' | 'barcode_type' | 'fields'
> = {
  width_mm: 100,
  height_mm: 50,
  columns: 1,
  gap_mm: 2,
  barcode_type: 'code128',
  fields: [
    { key: 'name', font_size: 11, bold: true },
    { key: 'barcode' },
    { key: 'sku', font_size: 10 },
  ],
}
